import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where
} from 'firebase/firestore';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentDots,
  faPenToSquare,
  faPlus,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db, storage } from '../../firebase';
import { logAdminActivity } from '../../utils/activityLogger';
import {
  formatRequisitionCurrency,
  formatRequisitionDate,
  getRequisitionStatusTone,
  normalizeRequisitionStatus,
  toDateValue
} from './requisitionHelpers';

const defaultFormState = {
  selectedCampus: '',
  itemTitle: '',
  itemDesc: '',
  numberOfUnits: '1',
  amountPerUnit: '',
  itemsNeededBy: ''
};

const resolveRequesterName = async (currentUser) => {
  if (!currentUser) {
    return {
      requesterName: 'Staff',
      requesterEmail: '',
      requesterId: ''
    };
  }

  let requesterName = currentUser.displayName || currentUser.email || 'Staff';

  try {
    const byUserId = await getDocs(
      query(collection(db, 'Admins'), where('userID', '==', currentUser.uid))
    );

    if (!byUserId.empty) {
      const adminData = byUserId.docs[0].data();
      requesterName = adminData.userName || adminData.userEmail || requesterName;
    } else if (currentUser.email) {
      const byEmail = await getDocs(
        query(collection(db, 'Admins'), where('userEmail', '==', currentUser.email))
      );

      if (!byEmail.empty) {
        const adminData = byEmail.docs[0].data();
        requesterName = adminData.userName || adminData.userEmail || requesterName;
      }
    }
  } catch (error) {
    console.error('Failed to resolve requester profile:', error);
  }

  return {
    requesterName,
    requesterEmail: currentUser.email || '',
    requesterId: currentUser.uid
  };
};

function RequiComp() {
  const auth = getAuth();
  const [modalShowUp, setModalShowUp] = useState(false);
  const [commentsModalShow, setCommentsModalShow] = useState(false);
  const [selectedComments, setSelectedComments] = useState(null);
  const [branchesList, setBranchesList] = useState([]);
  const [branchMapping, setBranchMapping] = useState({});
  const [requiLists, setRequiLists] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [requesterProfile, setRequesterProfile] = useState({
    requesterName: 'Staff',
    requesterEmail: '',
    requesterId: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [formState, setFormState] = useState(defaultFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRequisition, setEditingRequisition] = useState(null);
  const [receiptModalShow, setReceiptModalShow] = useState(false);
  const [selectedReceiptRequisition, setSelectedReceiptRequisition] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const fetchRequesterProfile = async () => {
      const profile = await resolveRequesterName(currentUser);
      setRequesterProfile(profile);
    };

    fetchRequesterProfile();
  }, [currentUser]);

  useEffect(() => {
    const fetchBranches = async () => {
      const q = query(collection(db, 'Branches'));
      const querySnapshot = await getDocs(q);
      const branches = [];
      const mapping = {};

      querySnapshot.forEach((branchDoc) => {
        const data = branchDoc.data();
        branches.push({ id: branchDoc.id, ...data });
        mapping[branchDoc.id] = data.branchName;
      });

      setBranchesList(branches);
      setBranchMapping(mapping);
      setFormState((prev) => ({
        ...prev,
        selectedCampus: prev.selectedCampus || branches[0]?.id || ''
      }));
    };

    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchRequisitions = async () => {
      const querySnapshot = await getDocs(collection(db, 'Requisitions'));
      const nextRequisitions = querySnapshot.docs
        .map((itemDoc) => ({
          id: itemDoc.id,
          ...itemDoc.data(),
          status: normalizeRequisitionStatus(itemDoc.data().status)
        }))
        .sort((a, b) => {
          const aDate = toDateValue(a.timeStamp)?.getTime() || 0;
          const bDate = toDateValue(b.timeStamp)?.getTime() || 0;
          return bDate - aDate;
        });

      setRequiLists(nextRequisitions);
    };

    fetchRequisitions();
  }, []);

  const summary = useMemo(() => {
    const counts = {
      total: requiLists.length,
      pending: 0,
      approved: 0,
      hold: 0
    };

    requiLists.forEach((item) => {
      const tone = getRequisitionStatusTone(item.status);

      if (tone === 'pending') {
        counts.pending += 1;
      }

      if (tone === 'approved' || tone === 'partial') {
        counts.approved += 1;
      }

      if (tone === 'hold' || tone === 'rejected') {
        counts.hold += 1;
      }

      if (tone === 'review') {
        counts.hold += 1;
      }
    });

    return counts;
  }, [requiLists]);

  const filteredRequisitions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return requiLists.filter((item) => {
      const matchesSearch =
        !normalizedSearch ||
        [
          item.itemTitle,
          item.itemDesc,
          item.requesterName,
          item.requesterEmail,
          branchMapping[item.selectedCampus]
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      const matchesStatus =
        statusFilter === 'All' || normalizeRequisitionStatus(item.status) === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [branchMapping, requiLists, searchTerm, statusFilter]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const resetFormState = () => {
    setFormState({
      ...defaultFormState,
      selectedCampus: branchesList[0]?.id || ''
    });
    setEditingRequisition(null);
  };

  const openCreateModal = () => {
    resetFormState();
    setModalShowUp(true);
  };

  const openEditModal = (item) => {
    const neededByDate = toDateValue(item.itemsNeededBy);

    setEditingRequisition(item);
    setFormState({
      selectedCampus: item.selectedCampus || branchesList[0]?.id || '',
      itemTitle: item.itemTitle || '',
      itemDesc: item.itemDesc || '',
      numberOfUnits: String(item.numberOfUnits || '1'),
      amountPerUnit: String(item.amountPerUnit || ''),
      itemsNeededBy: neededByDate
        ? new Date(neededByDate.getTime() - neededByDate.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16)
        : ''
    });
    setModalShowUp(true);
  };

  const submitRequisition = async () => {
    if (!currentUser) {
      swal('Error!', 'You need to be signed in to submit a requisition.', 'error');
      return;
    }

    const units = Number(formState.numberOfUnits);
    const amountPerUnit = Number(formState.amountPerUnit);
    const neededByDate = formState.itemsNeededBy ? new Date(formState.itemsNeededBy) : null;

    if (!formState.selectedCampus || !formState.itemTitle.trim() || !formState.itemDesc.trim()) {
      swal('Missing details', 'Please fill in the campus, item name, and description.', 'warning');
      return;
    }

    if (!Number.isFinite(units) || units <= 0 || !Number.isFinite(amountPerUnit) || amountPerUnit <= 0) {
      swal('Invalid amount', 'Units and amount per unit should be greater than zero.', 'warning');
      return;
    }

    if (!neededByDate || Number.isNaN(neededByDate.getTime())) {
      swal('Invalid date', 'Please choose when the item is needed by.', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const requisitionRef = editingRequisition
        ? doc(db, 'Requisitions', editingRequisition.id)
        : doc(collection(db, 'Requisitions'));
      const totalAmount = units * amountPerUnit;
      const payload = {
        selectedCampus: formState.selectedCampus,
        itemTitle: formState.itemTitle.trim(),
        itemDesc: formState.itemDesc.trim(),
        timeStamp: editingRequisition?.timeStamp || serverTimestamp(),
        numberOfUnits: units,
        amountPerUnit,
        itemsNeededBy: Timestamp.fromDate(neededByDate),
        docID: requisitionRef.id,
        totalAmount,
        status: editingRequisition ? 'Pending Approval' : 'Pending Approval',
        requesterId: editingRequisition?.requesterId || requesterProfile.requesterId,
        requesterName: editingRequisition?.requesterName || requesterProfile.requesterName,
        requesterEmail: editingRequisition?.requesterEmail || requesterProfile.requesterEmail,
        approvalComment: editingRequisition ? (editingRequisition.approvalComment || editingRequisition.comment || '') : '',
        approvalDate: editingRequisition?.approvalDate || null,
        approvedById: editingRequisition?.approvedById || '',
        approvedByName: editingRequisition?.approvedByName || '',
        approvedByEmail: editingRequisition?.approvedByEmail || '',
        receiptUrl: editingRequisition?.receiptUrl || '',
        receiptName: editingRequisition?.receiptName || '',
        receiptUploadedAt: editingRequisition?.receiptUploadedAt || null,
        updatedAt: serverTimestamp(),
        lastEditedById: currentUser.uid,
        lastEditedByName: requesterProfile.requesterName
      };

      await setDoc(requisitionRef, payload);

      setRequiLists((prev) => {
        const nextItem = {
          id: requisitionRef.id,
          ...payload,
          status: 'Pending Approval',
          timeStamp: editingRequisition ? editingRequisition.timeStamp : new Date(),
          updatedAt: new Date()
        };

        if (editingRequisition) {
          return prev.map((item) => (item.id === editingRequisition.id ? nextItem : item));
        }

        return [nextItem, ...prev];
      });

      logAdminActivity({
        action: editingRequisition ? 'Amended requisition' : 'Submitted requisition',
        details: `${editingRequisition ? 'Updated' : 'Requested'} ${payload.itemTitle} for ${branchMapping[payload.selectedCampus] || 'a campus'}.`,
        meta: {
          targetCollection: 'Requisitions',
          targetId: requisitionRef.id
        }
      });

      swal('Success!', editingRequisition ? 'Requisition updated and sent back for approval.' : 'Requisition submitted for approval.', 'success');
      setModalShowUp(false);
      resetFormState();
    } catch (error) {
      console.error('Failed to submit requisition:', error);
      swal('Error!', 'The requisition could not be submitted right now.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCommentsModal = (item) => {
    setSelectedComments(item);
    setCommentsModalShow(true);
  };

  const openReceiptModal = (item) => {
    setSelectedReceiptRequisition(item);
    setReceiptFile(null);
    setReceiptModalShow(true);
  };

  const submitReceipt = async () => {
    if (!selectedReceiptRequisition || !currentUser?.uid) {
      swal('Error!', 'You need to be signed in to upload a receipt.', 'error');
      return;
    }

    if (!receiptFile) {
      swal('Missing receipt', 'Please choose a receipt file first.', 'warning');
      return;
    }

    setIsUploadingReceipt(true);

    try {
      const safeName = receiptFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `requisition-receipts/${selectedReceiptRequisition.id}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, receiptFile);
      const receiptUrl = await getDownloadURL(storageRef);

      await setDoc(
        doc(db, 'Requisitions', selectedReceiptRequisition.id),
        {
          receiptUrl,
          receiptName: receiptFile.name,
          receiptPath: storagePath,
          receiptUploadedAt: serverTimestamp(),
          receiptUploadedById: currentUser.uid,
          receiptUploadedByName: requesterProfile.requesterName
        },
        { merge: true }
      );

      setRequiLists((prev) =>
        prev.map((item) =>
          item.id === selectedReceiptRequisition.id
            ? {
                ...item,
                receiptUrl,
                receiptName: receiptFile.name,
                receiptPath: storagePath,
                receiptUploadedAt: new Date(),
                receiptUploadedById: currentUser.uid,
                receiptUploadedByName: requesterProfile.requesterName
              }
            : item
        )
      );

      logAdminActivity({
        action: 'Uploaded requisition receipt',
        details: `Uploaded a receipt for ${selectedReceiptRequisition.itemTitle}.`,
        meta: {
          targetCollection: 'Requisitions',
          targetId: selectedReceiptRequisition.id
        }
      });

      swal('Success!', 'Receipt uploaded successfully.', 'success');
      setReceiptModalShow(false);
      setSelectedReceiptRequisition(null);
      setReceiptFile(null);
    } catch (error) {
      console.error('Failed to upload requisition receipt:', error);
      swal('Error!', 'The receipt could not be uploaded right now.', 'error');
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  return (
    <div className="teamsComponent requisitionPage">
      <div className="teamsTopBar requisitionTopBar">
        <div>
          <h5 className="tHide1">Requisitions</h5>
          <h5 className="tHide2">Requisitions</h5>
          <p className="requisitionIntro">
            Staff can log small operational purchases here and track whether finance has approved them.
          </p>
        </div>
        <button className="startOnlSessBtn" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} className="liveSessionIcon" /> New requisition
        </button>
        <button className="startOnlSessBtn2" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} className="liveSessionIcon" /> New requisition
        </button>
      </div>

      <div className="requisitionSummaryGrid">
        <article className="requisitionSummaryCard">
          <span>Total requests</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>Pending approval</span>
          <strong>{summary.pending}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>Approved / partial</span>
          <strong>{summary.approved}</strong>
        </article>
        <article className="requisitionSummaryCard">
          <span>On hold / rejected</span>
          <strong>{summary.hold}</strong>
        </article>
      </div>

      <div className="requisitionToolbar">
        <div className="requisitionFilterGroup">
          <label htmlFor="requisition-search">Search</label>
          <input
            id="requisition-search"
            type="text"
            placeholder="Item, requester, branch..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="requisitionFilterGroup">
          <label htmlFor="requisition-status-filter">Status</label>
          <select
            id="requisition-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="All">All statuses</option>
            <option value="Pending Approval">Pending approval</option>
            <option value="Review">Review</option>
            <option value="Approved">Approved</option>
            <option value="Partially Approved">Partially approved</option>
            <option value="On Hold">On hold</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {filteredRequisitions.length > 0 ? (
        <div className="requisitionTableWrap">
          <table className="requisitionTable">
            <thead>
              <tr>
                <th>Item</th>
                <th>Branch</th>
                <th>Requested by</th>
                <th>Submitted</th>
                <th>Needed by</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequisitions.map((item) => {
                const statusTone = getRequisitionStatusTone(item.status);
                const normalizedStatus = normalizeRequisitionStatus(item.status);
                const canEdit =
                  (normalizedStatus === 'Review' || normalizedStatus === 'Pending Approval') &&
                  currentUser?.uid &&
                  item.requesterId === currentUser.uid;
                const canUploadReceipt =
                  (normalizedStatus === 'Approved' || normalizedStatus === 'Partially Approved') &&
                  currentUser?.uid &&
                  item.requesterId === currentUser.uid &&
                  !item.receiptUrl;

                return (
                  <tr key={item.id}>
                    <td>
                      <div className="requisitionTableMainCell">
                        <strong>{item.itemTitle}</strong>
                        <span>{item.itemDesc}</span>
                      </div>
                    </td>
                    <td>{branchMapping[item.selectedCampus] || 'Campus not set'}</td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{item.requesterName || 'Staff member'}</strong>
                        <span>{item.requesterEmail || 'No email'}</span>
                      </div>
                    </td>
                    <td>{formatRequisitionDate(item.timeStamp)}</td>
                    <td>{formatRequisitionDate(item.itemsNeededBy)}</td>
                    <td>
                      <div className="requisitionTableSecondaryCell">
                        <strong>{formatRequisitionCurrency(item.totalAmount)}</strong>
                        <span>
                          {item.numberOfUnits} unit{Number(item.numberOfUnits) === 1 ? '' : 's'} at {formatRequisitionCurrency(item.amountPerUnit)} each
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`requisitionStatusPill requisitionStatusPill--${statusTone}`}>
                        {normalizedStatus}
                      </span>
                    </td>
                    <td>
                      <div className="requisitionTableActionCell">
                        <button
                          type="button"
                          className="requisitionGhostBtn requisitionGhostBtn--table"
                          onClick={() => openCommentsModal(item)}
                        >
                          <FontAwesomeIcon icon={faCommentDots} /> Notes
                        </button>
                        {canEdit ? (
                          <button
                            type="button"
                            className="requisitionGhostBtn requisitionGhostBtn--table"
                            onClick={() => openEditModal(item)}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} /> Amend
                          </button>
                        ) : null}
                        {canUploadReceipt ? (
                          <button
                            type="button"
                            className="requisitionGhostBtn requisitionGhostBtn--table"
                            onClick={() => openReceiptModal(item)}
                          >
                            <FontAwesomeIcon icon={faReceipt} /> Upload receipt
                          </button>
                        ) : null}
                        {item.receiptUrl ? (
                          <a
                            className="requisitionGhostBtn requisitionGhostBtn--table"
                            href={item.receiptUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FontAwesomeIcon icon={faReceipt} /> Receipt
                          </a>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="requisitionEmptyState">
          <h6>No requisitions found</h6>
          <p>Try a different search or create a new request for approval.</p>
        </div>
      )}

      <RequisitionRequestModal
        show={modalShowUp}
        onHide={() => {
          setModalShowUp(false);
          if (!isSubmitting) {
            resetFormState();
          }
        }}
        editingRequisition={editingRequisition}
        formState={formState}
        branchesList={branchesList}
        handleFormChange={handleFormChange}
        submitRequisition={submitRequisition}
        isSubmitting={isSubmitting}
      />

      <RequisitionCommentsModal
        show={commentsModalShow}
        onHide={() => setCommentsModalShow(false)}
        comments={selectedComments}
      />

      <ReceiptUploadModal
        show={receiptModalShow}
        onHide={() => {
          if (!isUploadingReceipt) {
            setReceiptModalShow(false);
            setSelectedReceiptRequisition(null);
            setReceiptFile(null);
          }
        }}
        requisition={selectedReceiptRequisition}
        receiptFile={receiptFile}
        setReceiptFile={setReceiptFile}
        onSubmit={submitReceipt}
        isSubmitting={isUploadingReceipt}
      />
    </div>
  );
}

function RequisitionRequestModal({
  show,
  onHide,
  editingRequisition,
  formState,
  branchesList,
  handleFormChange,
  submitRequisition,
  isSubmitting
}) {
  return (
    <Modal show={show} onHide={isSubmitting ? undefined : onHide} centered>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>{editingRequisition ? 'Amend requisition' : 'Request for an item'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Branch</Form.Label>
          <Form.Select
            name="selectedCampus"
            value={formState.selectedCampus}
            onChange={handleFormChange}
          >
            {branchesList.length > 0 ? (
              branchesList.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </option>
              ))
            ) : (
              <option value="">No branches available</option>
            )}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Item title</Form.Label>
          <Form.Control
            type="text"
            name="itemTitle"
            value={formState.itemTitle}
            onChange={handleFormChange}
            placeholder="Water, newspaper, office supplies..."
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Item description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="itemDesc"
            value={formState.itemDesc}
            onChange={handleFormChange}
            placeholder="Add a short explanation of what is needed."
          />
        </Form.Group>

        <div className="requisitionModalGrid">
          <Form.Group className="mb-3">
            <Form.Label>Number of units</Form.Label>
            <Form.Control
              type="number"
              min="1"
              name="numberOfUnits"
              value={formState.numberOfUnits}
              onChange={handleFormChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Amount per unit</Form.Label>
            <Form.Control
              type="number"
              min="1"
              name="amountPerUnit"
              value={formState.amountPerUnit}
              onChange={handleFormChange}
            />
          </Form.Group>
        </div>

        <Form.Group className="mb-0">
          <Form.Label>Item(s) needed by</Form.Label>
          <Form.Control
            type="datetime-local"
            name="itemsNeededBy"
            value={formState.itemsNeededBy}
            onChange={handleFormChange}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn btn-dark" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={submitRequisition} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span> Submitting...</span>
            </>
          ) : (
            editingRequisition ? 'Save changes' : 'Submit'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RequisitionCommentsModal({ show, onHide, comments }) {
  const normalizedStatus = normalizeRequisitionStatus(comments?.status);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Approval details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {comments ? (
          <div className="requisitionCommentWrap">
            <p><strong>Status:</strong> {normalizedStatus}</p>
            <p><strong>Comment:</strong> {comments.approvalComment || comments.comment || 'No approval note yet.'}</p>
            <p><strong>Reviewed by:</strong> {comments.approvedByName || comments.approvedByEmail || 'Finance team'}</p>
            <p><strong>Approval date:</strong> {formatRequisitionDate(comments.approvalDate, 'Not reviewed yet')}</p>
            <p><strong>Receipt:</strong> {comments.receiptUrl ? <a href={comments.receiptUrl} target="_blank" rel="noreferrer">{comments.receiptName || 'View receipt'}</a> : 'No receipt uploaded yet'}</p>
          </div>
        ) : (
          <p>No comments yet</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

function ReceiptUploadModal({
  show,
  onHide,
  requisition,
  receiptFile,
  setReceiptFile,
  onSubmit,
  isSubmitting
}) {
  return (
    <Modal show={show} onHide={isSubmitting ? undefined : onHide} centered>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Upload receipt</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {requisition ? (
          <div className="requisitionApprovalModalBody">
            <div className="requisitionApprovalPreview">
              <h6>{requisition.itemTitle}</h6>
              <p>{requisition.itemDesc}</p>
              <span>{formatRequisitionCurrency(requisition.totalAmount)}</span>
            </div>

            <Form.Group className="mb-0">
              <Form.Label>Receipt or M-Pesa screenshot</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,image/*"
                onChange={(event) => setReceiptFile(event.target.files?.[0] || null)}
              />
              <Form.Text muted>
                {receiptFile
                  ? `Selected: ${receiptFile.name}`
                  : requisition.receiptName
                    ? `Current receipt: ${requisition.receiptName}`
                    : 'Upload the purchase receipt or an M-Pesa purchase screenshot after the requisition has been approved and the item has been bought.'}
              </Form.Text>
            </Form.Group>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span> Uploading...</span>
            </>
          ) : (
            'Save receipt'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequiComp;
